import { sendErrorAlert } from '~/features/utils';
import { ALERT_VIEW_STATE } from '~/features/xstateSystem';
import { transformAddressToFormValues, transformFormValuesToAddress } from '../utils';
import {
  GetReferenceResponseWithJSONFormEvent,
  OnWizardPrevEvent,
  OnWizardUpdateEvent,
  PDFResponseEvent,
  SetMagicLinkEvent,
} from './events';
import { Context, Scope } from './types';
export const loadingTrue = (): Partial<Context> => {
  return {
    loading: true,
  };
};

export const loadingFalse = (): Partial<Context> => {
  return {
    loading: false,
  };
};

export const setMagicLink = (scope: Scope): Partial<Context> => {
  const { payload } = scope.event as SetMagicLinkEvent;
  return {
    magicLink: payload.magicLink,
  };
};

export const setPeerReference = (scope: Scope): Partial<Context> => {
  const { output } = scope.event as GetReferenceResponseWithJSONFormEvent;
  const peerReference = output.peerReference;
  peerReference.jsonForm = output.jsonForm;
  const optionalString = (value: string | null | undefined) => value || '';
  const relationshipType = optionalString(peerReference.relationshipType);

  // The questionnaire is only persisted server-side on final submit, so a GET
  // triggered by patching an *earlier* page (e.g. correcting contact info after
  // navigating back) returns no formData. Keep the locally-captured draft in
  // that case so answers entered before navigating back aren't wiped (MCV-1488).
  const serverJSONFormData = peerReference.formInstance?.formData;
  const hasServerJSONFormData = !!serverJSONFormData && Object.keys(serverJSONFormData).length > 0;

  return {
    form: output.jsonForm?.jsonForm,
    address: transformAddressToFormValues(output.address),
    formUi: output.jsonForm.jsonFormUi,
    peerReference: peerReference,
    personalInfo: {
      emailAddress: peerReference.emailAddress,
      faxNum: optionalString(peerReference.faxNum),
      firstName: peerReference.firstName,
      lastName: peerReference.lastName,
      occupation: optionalString(peerReference.occupation),
      phoneNum: optionalString(peerReference.phoneNum),
      prefixName: optionalString(peerReference.prefixName),
      relationshipType: {
        label: relationshipType,
        value: relationshipType,
      },
      shareFormFlag: peerReference.shareFormFlag ?? false,
      suffixName: optionalString(peerReference.suffixName),
    },
    defaultJSONFormData: hasServerJSONFormData
      ? serverJSONFormData
      : scope.context.defaultJSONFormData || {},
  };
};

export const updatePeerRef = (scope: Scope): Partial<Context> => {
  const { values } = scope.event as OnWizardUpdateEvent;
  return {
    peerReference: { ...scope.context.peerReference, ...values },
  };
};

// Persist the current page's live form values into the matching context seed
// field so they survive the page component unmounting on backward navigation.
// Without this, ON_WIZ_PREV_PAGE only decremented the step and the answers were
// lost when the previous page remounted from stale context (MCV-1488).
export const saveWizardDraft = (scope: Scope): Partial<Context> => {
  const { draft } = scope.event as OnWizardPrevEvent;
  if (!draft) return {};
  switch (draft.kind) {
    case 'personal':
      return { personalInfo: draft.values };
    case 'address':
      // Also fold the draft into peerReference.address so the next PATCH carries
      // it to the server. Otherwise a PATCH/GET triggered by an *earlier* page
      // (e.g. correcting personal info) round-trips the stale server address and
      // wipes these edits when the address page remounts (MCV-1488). The
      // questionnaire can't use this route — it isn't persisted until final
      // submit — so it relies on the setPeerReference fallback instead.
      return {
        address: draft.values,
        peerReference: {
          ...scope.context.peerReference,
          address: transformFormValuesToAddress(draft.values),
        },
      };
    case 'json':
      return { defaultJSONFormData: draft.values };
    default:
      return {};
  }
};

export const setNextStep = (scope: Scope): Partial<Context> => {
  return {
    step: scope.context.step + 1,
  };
};

export const setPrevStep = (scope: Scope): Partial<Context> => {
  return {
    step: scope.context.step - 1,
  };
};

export const setNotFound = (): Partial<Context> => ({ notFound: true });

// TODO: MCV--447-Breakout loadPDF Action to be reuseable
export const loadPdf = (scope: Scope) => {
  const { output } = scope.event as PDFResponseEvent;

  const blob = new Blob([output], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const popup = window.open(url, '_blank');

  if (!popup || popup.closed || typeof popup.closed === 'undefined') {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Peer_Reference.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
    scope.system.get(ALERT_VIEW_STATE)?.send(
      sendErrorAlert({
        text: 'Pop-up blocked',
        subText: 'A download has started. Enable pop-ups for this site, then try again.',
      })
    );
  }

  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};
